import { useMemo, type ReactNode } from 'react';
import { useAppStore, selectDetections, selectPresentDetections, selectReviewDetections, selectActiveProfile, selectProfileHits, selectCertificationContradictions, selectAllProfileVerdicts, selectPrioritizedPALs, selectSevereAllergenDetected } from '@/store';
import { useShallow } from 'zustand/react/shallow';
import type { SeverityLevel } from '@/types/profile';
import { categoryLabel } from '@allergen-lens/shared-logic';
import { ShieldCheck, AlertTriangle, Check, Info } from 'lucide-react';
import { AllergenCard } from './AllergenCard';
import { SubstitutionPanel } from './SubstitutionPanel';
import styles from './ResultsPanel.module.css';

function getSeverityClass(severity: SeverityLevel | undefined): string {
  switch (severity) {
    case 'must_avoid': return styles.severityMustAvoid ?? '';
    case 'may_tolerate': return styles.severityMayTolerate ?? '';
    case 'cross_contact_alert': return styles.severityCrossContact ?? '';
    default: return '';
  }
}

const severityRank: Record<SeverityLevel, number> = {
  must_avoid: 0,
  cross_contact_alert: 1,
  may_tolerate: 2,
};

export function ResultsPanel() {
  const detections = useAppStore(selectDetections);
  const presentDetections = useAppStore(useShallow(selectPresentDetections));
  const reviewDetections = useAppStore(useShallow(selectReviewDetections));
  const showReviewOnly = useAppStore((s) => s.showReviewOnly);
  const setShowReviewOnly = useAppStore((s) => s.setShowReviewOnly);
  const profiles = useAppStore((s) => s.profiles);
  const activeProfileId = useAppStore((s) => s.activeProfileId);
  const activeProfile = useAppStore(selectActiveProfile);
  const profileHits = useAppStore(useShallow(selectProfileHits));
  const severeDetected = useAppStore(selectSevereAllergenDetected);
  const certificationMatches = useAppStore((s) => s.certificationMatches);
  const palDetections = useAppStore((s) => s.palDetections);
  const contradictions = useMemo(
    () => selectCertificationContradictions(useAppStore.getState()),
    [detections, certificationMatches]
  );
  const allProfileVerdicts = useMemo(
    () => selectAllProfileVerdicts(useAppStore.getState()),
    [detections, profiles, activeProfileId]
  );
  const classifiedPALs = useMemo(
    () => selectPrioritizedPALs(useAppStore.getState()),
    [palDetections, profiles, activeProfileId]
  );

  const severityMap = useMemo(() => {
    const active = profiles.find((p) => p.id === activeProfileId);
    const map = new Map<string, SeverityLevel>();
    if (active?.allergenSeverities) {
      for (const s of active.allergenSeverities) {
        map.set(s.allergenId, s.severity);
      }
    }
    return map;
  }, [profiles, activeProfileId]);

  if (detections.length === 0) {
    return (
      <div className={styles.resultsPanel}>
        <div className={styles.emptyState}>
          <ShieldCheck size={36} className={styles.emptyIcon} aria-hidden />
          <span className={styles.emptyHeading}>Your allergen results will appear here</span>
          <span className={styles.emptyHint}>
            Paste an ingredient list on the left to get an instant safety verdict,
            detected allergens, and precautionary warnings.
          </span>
          <ul className={styles.emptySteps}>
            <li><Check size={14} aria-hidden /> Safety verdict at a glance</li>
            <li><Check size={14} aria-hidden /> Allergens sorted by your severity</li>
            <li><Check size={14} aria-hidden /> &ldquo;May contain&rdquo; warnings flagged</li>
          </ul>
          <span className={styles.emptyCta}>
            New here? Try an example from the input panel &rarr;
          </span>
        </div>
      </div>
    );
  }

  // Sort by severity: must_avoid → cross_contact_alert → may_tolerate → unranked last.
  const displayedDetections = (showReviewOnly ? reviewDetections : detections)
    .slice()
    .sort((a, b) => {
      const ra = severityMap.get(a.allergenId);
      const rb = severityMap.get(b.allergenId);
      const va = ra === undefined ? Number.MAX_SAFE_INTEGER : severityRank[ra];
      const vb = rb === undefined ? Number.MAX_SAFE_INTEGER : severityRank[rb];
      return va - vb;
    });

  // Profile-aware verdict banner
  const hitCount = profileHits.length;
  let verdictBanner: ReactNode;
  if (activeProfile && hitCount > 0) {
    const hitNames = [...new Set(profileHits.map((d) => categoryLabel(d.allergenCategory)))];
    verdictBanner = (
      <div className={`${styles.verdictBanner} ${styles.verdictCaution}`} role="alert">
        <AlertTriangle size={18} className={styles.verdictIcon} />
        <span>UNSAFE — contains {hitCount} of your allergen{hitCount > 1 ? 's' : ''}: {hitNames.join(', ')}</span>
      </div>
    );
  } else if (activeProfile && presentDetections.length > 0) {
    verdictBanner = (
      <div className={`${styles.verdictBanner} ${styles.verdictWarning}`} role="status">
        <Info size={18} className={styles.verdictIcon} />
        <span>{presentDetections.length} allergen{presentDetections.length > 1 ? 's' : ''} detected — none match your profile</span>
      </div>
    );
  } else if (presentDetections.length === 0) {
    verdictBanner = (
      <div className={styles.verdictBanner} role="status">
        <ShieldCheck size={18} className={styles.verdictIcon} />
        <span>Safe — no allergens detected</span>
      </div>
    );
  } else {
    verdictBanner = (
      <div className={`${styles.verdictBanner} ${styles.verdictCaution}`} role="alert">
        <AlertTriangle size={18} className={styles.verdictIcon} />
        <span>{presentDetections.length} allergen{presentDetections.length > 1 ? 's' : ''} detected — CAUTION</span>
      </div>
    );
  }

  return (
    <div className={styles.resultsPanel}>
      {verdictBanner}

      {activeProfile && profiles.length > 1 && allProfileVerdicts.length > 0 && (
        <div className={styles.multiProfileRow}>
          {allProfileVerdicts.map((v) => (
            <span
              key={v.profileId}
              className={`${styles.profileChip} ${v.hits.length > 0 ? styles.profileChipUnsafe ?? '' : styles.profileChipSafe ?? ''}`}
            >
              {v.hits.length > 0
                ? `⚠ ${v.profileName}: UNSAFE (${v.hits.map((h) => categoryLabel(h.allergenCategory)).join(', ')})`
                : `✓ ${v.profileName}: safe`}
            </span>
          ))}
        </div>
      )}

      {severeDetected && (
        <a
          href="#results/er-locator"
          className={styles.erCTA}
          role="alert"
        >
          <AlertTriangle size={18} />
          Severe allergen detected — find nearest emergency room
        </a>
      )}

      {contradictions.length > 0 && (
        <div className={styles.contradictionAlert} role="alert">
          <AlertTriangle size={16} className={styles.verdictIcon} />
          <div>
            {contradictions.map((c) => (
              <div key={c.certification}>
                &ldquo;{c.certification}&rdquo; claims {c.contradictedAllergens.map(categoryLabel).join(', ')} free, but detected in ingredients
              </div>
            ))}
          </div>
        </div>
      )}

      {classifiedPALs.length > 0 && (
        <div className={styles.palList}>
          {[...classifiedPALs]
            .sort((a, b) => {
              const rank: Record<string, number> = { critical: 0, watch: 1, informational: 2 };
              return rank[a.priority] - rank[b.priority];
            })
            .map(({ pal, priority }) => (
              <div
                key={pal.id}
                className={`${styles.palWarning} ${styles[`pal_${priority}`] ?? ''}`}
              >
                <AlertTriangle size={14} className={styles.palIcon} />
                <span className={styles.palText}>{pal.statement}</span>
                {priority === 'critical' && <span className={styles.palBadge}>Critical for you</span>}
                {priority === 'watch' && <span className={styles.palBadge}>Watch</span>}
              </div>
            ))}
        </div>
      )}

      <div className={styles.controls}>
        {reviewDetections.length > 0 && (
          <label className={styles.toggle} title="Show only items needing validation.">
            <input
              id="rp-review-only"
              type="checkbox"
              className={styles.toggleInput}
              checked={showReviewOnly}
              onChange={(e) => setShowReviewOnly(e.target.checked)}
            />
            Validation only ({reviewDetections.length})
          </label>
        )}
      </div>

      <div className={styles.cardList}>
        {displayedDetections.map((det) => {
          const severity = severityMap.get(det.allergenId);
          const severityClass = getSeverityClass(severity);
          return (
            <div key={det.id} className={severityClass}>
              <AllergenCard detection={det} {...(severity !== undefined ? { severity } : {})} />
            </div>
          );
        })}
      </div>

      {detections.length > 0 && (
        <div className={styles.substitutions}>
          <h3 className={styles.subHeading}>Substitutions</h3>
          <SubstitutionPanel />
        </div>
      )}
    </div>
  );
}
